import { Download } from "lucide-react";

import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Icon } from "@/components/Icon";
import { IMAGES, CAP_STATEMENT, INDUSTRIES_SERVED } from "@/data/content";
import { CAPABILITY_PDF_URL } from "@/lib/api";

function Block({ title, children }) {
  return (
    <Reveal>
      <div className="py-10 border-b border-hairline">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <h2 className="lg:col-span-4 font-heading text-2xl md:text-3xl font-bold text-navy tracking-tight">{title}</h2>
          <div className="lg:col-span-8 text-steel leading-relaxed text-lg">{children}</div>
        </div>
      </div>
    </Reveal>
  );
}

export default function CapabilityStatement() {
  useSeo({
    title: "Capability Statement",
    description:
      "Meridian Vanguard Solutions capability statement — company overview, core competencies, differentiators, corporate experience, and government information.",
    path: "/capability-statement",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "Capability Statement", path: "/capability-statement" }],
  });

  return (
    <>
      <PageHero
        eyebrow="Overview"
        titleLines={["Capability Statement"]}
        subtitle="Core competencies, differentiators, and government information for Meridian Vanguard Solutions, LLC — a Service-Disabled Veteran-Owned Small Business."
        image={IMAGES.dashboard}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Capability Statement", path: "/capability-statement" }]}
      />

      {/* Brand logo banner */}
      <section className="bg-white border-b border-hairline py-14 md:py-16" data-testid="capstatement-logo-banner">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 flex justify-center">
          <img
            src="/logo.png"
            alt="Meridian Vanguard Solutions, LLC"
            className="h-24 md:h-32 lg:h-40 w-auto"
          />
        </div>
      </section>

      {/* Download bar */}
      <section className="bg-navy">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="text-accent2 text-sm font-semibold uppercase tracking-[0.2em]">Meridian Vanguard Solutions, LLC</p>
            <p className="mt-1 text-white/80">Veteran-Led SDVOSB • Strategy • Innovation • Execution</p>
          </div>
          <a
            href={CAPABILITY_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="capstatement-download-top"
            className="inline-flex items-center gap-3 rounded-xl bg-white text-navy px-7 py-3.5 font-semibold hover:bg-accent2 hover:text-white transition-colors"
          >
            <Download size={18} /> Download PDF
          </a>
        </div>
      </section>

      {/* Positioning statement */}
      <section className="bg-lightgray py-16 md:py-20">
        <div className="w-full max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="border-l-4 border-accent2 pl-6 md:pl-8">
              <p className="font-heading text-2xl md:text-3xl font-semibold text-navy leading-snug">
                “{CAP_STATEMENT.positioning}”
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <Block title="Company Overview">
            <p>{CAP_STATEMENT.overview}</p>
          </Block>
        </div>
      </section>

      {/* Core competencies — categorized cards */}
      <section className="bg-lightgray py-24 md:py-28" data-testid="cap-competencies">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="What We Do" title="Core Competencies" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {CAP_STATEMENT.competencyGroups.map((g) => (
              <Reveal key={g.title}>
                <div className="h-full rounded-xl bg-white border border-hairline p-8">
                  <div className="h-14 w-14 rounded-xl bg-navy flex items-center justify-center">
                    <Icon name={g.icon} className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-navy">{g.title}</h3>
                  <ul className="mt-5 space-y-3">
                    {g.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-navy font-medium">
                        <Icon name="CheckCircle2" className="h-5 w-5 text-accent2 mt-0.5 shrink-0" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Meridian Vanguard Solutions */}
      <section className="bg-white py-24 md:py-28" data-testid="cap-why">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="Differentiators" title="Why Meridian Vanguard Solutions" />
          <StaggerGroup stagger={0.06} className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {CAP_STATEMENT.whyMeridian.map((w, i) => (
              <StaggerItem key={i}>
                <div className="group h-full flex items-start gap-5 rounded-xl border border-hairline bg-white p-7 transition-all duration-300 hover:border-navy/20 hover:shadow-[0_14px_32px_rgba(10,35,66,0.08)]">
                  <span className="h-12 w-12 shrink-0 rounded-xl bg-lightgray flex items-center justify-center transition-colors group-hover:bg-navy">
                    <Icon name={w.icon} className="h-6 w-6 text-navy transition-colors group-hover:text-white" />
                  </span>
                  <p className="text-steel leading-relaxed text-[15px] pt-1">{w.text}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Mission + Value proposition */}
      <section className="bg-navy py-24 md:py-28" data-testid="cap-mission-value">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="h-full rounded-xl bg-white/[0.06] border border-white/10 p-10">
              <div className="h-14 w-14 rounded-xl bg-accent2 flex items-center justify-center">
                <Icon name="Compass" className="h-7 w-7 text-white" />
              </div>
              <h3 className="mt-6 text-sm font-semibold uppercase tracking-[0.22em] text-accent2">Mission</h3>
              <p className="mt-4 font-heading text-2xl md:text-3xl font-semibold text-white leading-snug">
                “{CAP_STATEMENT.mission}”
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-xl bg-white/[0.06] border border-white/10 p-10">
              <div className="h-14 w-14 rounded-xl bg-accent2 flex items-center justify-center">
                <Icon name="Gem" className="h-7 w-7 text-white" />
              </div>
              <h3 className="mt-6 text-sm font-semibold uppercase tracking-[0.22em] text-accent2">Our Value</h3>
              <p className="mt-4 font-heading text-2xl md:text-3xl font-semibold text-white leading-snug">
                “{CAP_STATEMENT.valueProp}”
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Industries served */}
      <section className="bg-white py-24 md:py-28" data-testid="cap-industries">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="Sectors" title="Industries Served" />
          <StaggerGroup stagger={0.05} className="mt-14 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {INDUSTRIES_SERVED.map((ind) => (
              <StaggerItem key={ind.label}>
                <div className="group flex items-center gap-4 rounded-xl bg-lightgray border border-transparent p-5 transition-all duration-300 hover:bg-white hover:border-hairline hover:shadow-[0_10px_24px_rgba(10,35,66,0.07)]">
                  <Icon name={ind.icon} className="h-6 w-6 text-accent2 shrink-0" />
                  <span className="font-medium text-navy text-[15px] leading-tight">{ind.label}</span>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <section className="bg-lightgray py-20 md:py-28">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <div className="rounded-xl bg-white border border-hairline p-8 md:p-12">
            <Block title="Corporate Experience">
              <p>{CAP_STATEMENT.corporateExperience}</p>
            </Block>

            {/* Government information */}
            <Reveal>
              <div className="py-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <h2 className="lg:col-span-4 font-heading text-2xl md:text-3xl font-bold text-navy tracking-tight">Government Information</h2>
                  <div className="lg:col-span-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {CAP_STATEMENT.govInfo.map((g) => (
                        <div key={g.label} data-testid={`gov-info-${g.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-xl bg-lightgray border border-hairline p-5">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-steel">{g.label}</p>
                          <p className="mt-2 font-heading font-semibold text-navy">{g.value}</p>
                        </div>
                      ))}
                    </div>
                    <p className="mt-4 text-sm text-steel">Government registration identifiers will be updated upon assignment.</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white pb-24 md:pb-28 pt-4">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          {/* Contact info */}
          <Reveal>
            <div className="py-10 border-b border-hairline">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <h2 className="lg:col-span-4 font-heading text-2xl md:text-3xl font-bold text-navy tracking-tight">Contact Information</h2>
                <div className="lg:col-span-8 text-steel leading-relaxed text-lg">
                  <p>Email: <a href="mailto:info@meridianvanguardsolutions.com" className="text-accent2 hover:underline">info@meridianvanguardsolutions.com</a></p>
                  <p className="mt-1">Phone: Coming Soon</p>
                  <p className="mt-1">Office: Virtual Office Coming Soon</p>
                  <p className="mt-1">Hours: Monday–Friday, 8:00 AM–5:00 PM</p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Big download button */}
          <Reveal>
            <div className="mt-8 rounded-xl bg-lightgray border border-hairline p-10 md:p-14 text-center">
              <h3 className="font-heading text-3xl font-bold text-navy tracking-tight">Download the Full Capability Statement</h3>
              <p className="mt-4 text-steel text-lg max-w-2xl mx-auto">Get a printable, government-ready overview of our competencies and qualifications.</p>
              <a
                href={CAPABILITY_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="capstatement-download-main"
                className="mt-8 inline-flex items-center gap-3 rounded-xl bg-navy text-white px-10 py-4 font-semibold hover:bg-accent2 transition-colors duration-300"
              >
                <Download size={20} /> Download PDF
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
